/**
 * Iter-237 — Creator Affiliate Dashboard
 * Route: /talent/affiliate
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Link as LinkIcon, Tag, Plus, Copy, ExternalLink,
  Loader2, Edit3, Sparkles, Trophy, CheckCircle2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';

const fmt = (n) => (n || 0).toLocaleString();
const money = (cents) =>
  cents == null ? '—'
    : `$${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function CreatorAffiliateDashboard() {
  const navigate = useNavigate();
  const [perf, setPerf] = useState(null);
  const [links, setLinks] = useState([]);
  const [codes, setCodes] = useState([]);
  const [bonuses, setBonuses] = useState(null);
  const [celebration, setCelebration] = useState(null);  // first unseen goal hit
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, l, c, b, gh] = await Promise.all([
        api.get('/creator-affiliate/performance?range=90d'),
        api.get('/creator-affiliate/links'),
        api.get('/creator-affiliate/promo-codes'),
        api.get('/creator-bonuses/progress').catch(() => ({ data: null })),
        api.get('/creator-bonuses/goal-hits').catch(() => ({ data: { events: [] } })),
      ]);
      setPerf(p.data);
      setLinks(l.data.links || []);
      setCodes(c.data.promo_codes || []);
      setBonuses(b.data);
      // Surface first unseen goal hit as a celebration modal
      const events = gh.data?.events || [];
      const unseen = events.find((e) => !e.seen_at);
      if (unseen) setCelebration(unseen);
      // discover clients the creator has worked with
      const ids = new Set();
      (l.data.links || []).forEach((x) => x.client_name && ids.add(x.client_name));
      (c.data.promo_codes || []).forEach((x) => x.client_name && ids.add(x.client_name));
      setClients([...ids]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load affiliate data');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const newLink = useCallback(async () => {
    const targetUrl = window.prompt('Affiliate target URL (e.g. https://brand.com/product):');
    if (!targetUrl) return;
    const clientId = window.prompt('Client user id (ask your iEvntür rep if unsure):');
    if (!clientId) return;
    try {
      await api.post('/creator-affiliate/links', { target_url: targetUrl, client_id: clientId });
      toast.success('Affiliate link created');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  }, [load]);

  const newCode = useCallback(async () => {
    const clientId = window.prompt('Client user id:');
    if (!clientId) return;
    const discount = parseInt(window.prompt('Discount % (default 10):') || '10', 10) || 10;
    try {
      await api.post('/creator-affiliate/promo-codes', {
        client_id: clientId, discount_percent: discount,
      });
      toast.success('Promo code created');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  }, [load]);

  const rename = useCallback(async (codeId, current, hasBeenRenamed) => {
    if (hasBeenRenamed) {
      toast.error('You can only rename a promo code once');
      return;
    }
    const v = window.prompt(`Rename ${current} to (UPPERCASE, letters+digits only):`)?.toUpperCase();
    if (!v) return;
    try {
      await api.patch(`/creator-affiliate/promo-codes/${codeId}/rename`, { code: v });
      toast.success(`Renamed to ${v}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  }, [load]);

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success('Copied');
  };

  const dismissCelebration = useCallback(async () => {
    setCelebration(null);
    try {
      await api.post('/creator-bonuses/goal-hits/seen');
    } catch (e) { /* silent */ }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  const fullDomain = (window.location.origin || '').replace(/\/+$/, '');

  return (
    <div className="min-h-screen bg-slate-50 pb-20" data-testid="creator-affiliate-dashboard">
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <button onClick={() => navigate('/talent/profile')}
            className="text-cyan-300 text-sm flex items-center mb-3 hover:opacity-80"
            data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Profile
          </button>
          <div className="text-xs uppercase tracking-widest text-cyan-300/70">
            Creator Affiliate Program
          </div>
          <h1 className="text-2xl md:text-4xl font-bold mt-1">Affiliate Performance</h1>
          <p className="text-sm text-slate-300 mt-1">
            Tracking links + promo codes that convert audience interest into measurable revenue for your brand partners.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-6">
          <Kpi label="Clicks" value={fmt(perf?.clicks)} testid="kpi-clicks" />
          <Kpi label="Unique Visitors" value={fmt(perf?.unique_visitors)} testid="kpi-unique" />
          <Kpi label="Promo Uses" value={fmt(perf?.promo_uses)} testid="kpi-promo-uses" />
          <Kpi label="Orders" value={fmt(perf?.orders)} testid="kpi-orders" />
          <Kpi label="Revenue Driven" value={money(perf?.revenue_cents)} testid="kpi-revenue" />
          <Kpi label="Conv. Rate"
            value={perf?.conversion_rate_pct != null ? `${perf.conversion_rate_pct.toFixed(2)}%` : '—'}
            testid="kpi-cr" />
        </div>
        {perf?.roas != null && (
          <div className="mt-3 text-sm text-slate-700">
            Estimated ROAS for your work: <b>{perf.roas.toFixed(2)}×</b>
            <span className="text-xs text-slate-500"> · based on payouts from completed shifts</span>
          </div>
        )}

        {/* Iter-238 — Pay-for-Performance Bonuses */}
        {bonuses && (bonuses.progress?.length > 0 || bonuses.earnings?.length > 0) && (
          <section className="mt-10" data-testid="creator-bonus-section">
            <div className="flex items-center gap-2 mb-3">
              <Trophy className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-bold text-slate-900">Pay-for-Performance Bonuses</h2>
            </div>

            {/* Earnings summary tiles */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Kpi label="Total Earned"
                value={money(bonuses.total_earned_cents)}
                testid="bonus-total-earned" />
              <Kpi label="Pending Approval"
                value={money(bonuses.total_pending_cents)}
                testid="bonus-pending" />
              <Kpi label="Approved"
                value={money(bonuses.total_approved_cents)}
                testid="bonus-approved" />
              <Kpi label="Paid Out"
                value={money(bonuses.total_paid_cents)}
                testid="bonus-paid" />
            </div>

            {/* Active tier progress bars */}
            {bonuses.progress?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4"
                data-testid="bonus-progress-list">
                <div className="text-sm font-semibold text-slate-700 mb-3">
                  Active Bonus Targets
                </div>
                <ul className="space-y-3">
                  {bonuses.progress.map((t, i) => (
                    <li key={t.tier_id} data-testid={`bonus-tier-${i}`}>
                      <div className="flex justify-between text-sm mb-1">
                        <div>
                          <span className="font-medium text-slate-900">{t.label}</span>
                          {t.earned && (
                            <span className="ml-2 inline-flex items-center text-xs text-emerald-600 font-semibold">
                              <CheckCircle2 className="w-3.5 h-3.5 mr-0.5" /> EARNED
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <b className="text-slate-900">{money(t.bonus_cents)}</b>
                          <span className="text-xs text-slate-500 ml-1">
                            · {t.current_value}/{t.threshold} {t.metric.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            t.earned ? 'bg-emerald-500' : 'bg-amber-400'
                          }`}
                          style={{ width: `${t.percent}%` }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Earnings history */}
            {bonuses.earnings?.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-y-hidden overflow-x-auto"
                data-testid="bonus-earnings-list">
                <table className="w-full text-sm min-w-[520px]">
                  <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-4 py-2">Bonus</th>
                      <th className="text-right px-3 py-2">Amount</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bonuses.earnings.map((e, i) => (
                      <tr key={e.id} className="border-t border-slate-100"
                        data-testid={`bonus-earning-${i}`}>
                        <td className="px-4 py-2 font-medium text-slate-900">{e.label}</td>
                        <td className="px-3 py-2 text-right font-bold text-slate-900">{money(e.bonus_cents)}</td>
                        <td className="px-3 py-2">
                          <Badge className={
                            e.status === 'paid' ? 'bg-emerald-100 text-emerald-900'
                              : e.status === 'approved' ? 'bg-cyan-100 text-cyan-900'
                              : e.status === 'cancelled' ? 'bg-slate-100 text-slate-500'
                              : 'bg-amber-100 text-amber-900'
                          }>{e.status.toUpperCase()}</Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {e.paid_at ? `paid ${new Date(e.paid_at).toLocaleDateString()}`
                            : e.approved_at ? `approved ${new Date(e.approved_at).toLocaleDateString()}`
                            : `earned ${new Date(e.computed_at).toLocaleDateString()}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Links */}
        <div className="flex items-center justify-between mt-10 mb-3">
          <div className="flex items-center gap-2">
            <LinkIcon className="w-5 h-5 text-cyan-500" />
            <h2 className="text-lg font-bold text-slate-900">My Affiliate Links</h2>
          </div>
          <Button onClick={newLink} className="bg-slate-900 hover:bg-slate-800" data-testid="new-link-btn">
            <Plus className="w-4 h-4 mr-1" /> New Link
          </Button>
        </div>
        {links.length === 0 && (
          <p className="text-sm text-slate-500">
            You don't have any affiliate links yet. Ask your iEvntür rep to enable creator
            attribution on your active campaigns, then click "New Link".
          </p>
        )}
        <div className="space-y-3" data-testid="affiliate-links-list">
          {links.map((l, i) => (
            <div key={l.id} className="bg-white border border-slate-200 rounded-xl p-4"
              data-testid={`affiliate-link-${i}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{l.client_name || 'Brand'}</div>
                  <div className="flex flex-wrap items-center gap-2 text-sm mt-1">
                    <span className="font-mono bg-slate-100 px-2 py-0.5 rounded">
                      {fullDomain}/api/r/{l.slug}
                    </span>
                    <button onClick={() => copy(`${fullDomain}/api/r/${l.slug}`)}
                      className="text-cyan-700 hover:underline text-xs inline-flex items-center"
                      data-testid={`copy-link-${i}`}>
                      <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                    </button>
                    <a href={`/api/r/${l.slug}`} target="_blank" rel="noreferrer"
                      className="text-cyan-700 hover:underline text-xs inline-flex items-center">
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Preview
                    </a>
                    <Badge variant={l.status === 'active' ? 'default' : 'outline'}>{l.status}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">→ {l.target_url}</div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center min-w-[260px]">
                  <Mini label="Clicks" value={fmt(l.clicks)} />
                  <Mini label="Conv" value={fmt(l.conversions)} />
                  <Mini label="Revenue" value={money(l.revenue_cents)} />
                  <Mini label="CR"
                    value={l.conversion_rate_pct != null ? `${l.conversion_rate_pct.toFixed(1)}%` : '—'} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Promo codes */}
        <div className="flex items-center justify-between mt-10 mb-3">
          <div className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-cyan-500" />
            <h2 className="text-lg font-bold text-slate-900">My Promo Codes</h2>
          </div>
          <Button onClick={newCode} className="bg-slate-900 hover:bg-slate-800" data-testid="new-code-btn">
            <Plus className="w-4 h-4 mr-1" /> New Code
          </Button>
        </div>
        {codes.length === 0 && (
          <p className="text-sm text-slate-500">
            No promo codes yet. Codes auto-generate as <code>FIRSTNAME + discount%</code> (e.g. <b>SARAH15</b>).
            You can rename your code one time.
          </p>
        )}
        <div className="space-y-3" data-testid="promo-codes-list">
          {codes.map((c, i) => (
            <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-4"
              data-testid={`promo-code-${i}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{c.client_name || 'Brand'}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-mono text-lg bg-slate-100 px-3 py-1 rounded text-slate-900">
                      {c.code}
                    </span>
                    <Badge variant="outline">
                      {c.discount_percent ? `${c.discount_percent}% off` : 'flat'}
                    </Badge>
                    <button onClick={() => copy(c.code)}
                      className="text-cyan-700 hover:underline text-xs inline-flex items-center">
                      <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                    </button>
                    <button onClick={() => rename(c.id, c.code, c.has_been_renamed)}
                      className={`text-xs inline-flex items-center ${
                        c.has_been_renamed ? 'text-slate-400 cursor-not-allowed' : 'text-cyan-700 hover:underline'
                      }`}
                      disabled={c.has_been_renamed}
                      data-testid={`rename-promo-${i}`}>
                      <Edit3 className="w-3.5 h-3.5 mr-1" />
                      {c.has_been_renamed ? 'Renamed once' : 'Rename (one-time)'}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center min-w-[260px]">
                  <Mini label="Uses" value={fmt(c.uses)} />
                  <Mini label="Orders" value={fmt(c.orders)} />
                  <Mini label="Revenue" value={money(c.revenue_cents)} />
                  <Mini label="AOV" value={money(c.average_order_value_cents)} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 p-4 bg-cyan-50 border border-cyan-200 rounded-xl flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-cyan-700 mt-0.5" />
          <div className="text-sm text-cyan-900">
            <b>Pro tip:</b> Push the same link AND code in every post. We attribute conversions to whichever
            signal arrives first, so the more places you share, the higher your reported revenue.
          </div>
        </div>
      </div>

      {/* Iter-239 — Goal Hit celebration modal */}
      {celebration && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 flex items-center justify-center p-4"
          data-testid="celebration-modal"
          onClick={(e) => e.target === e.currentTarget && dismissCelebration()}>
          <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center shadow-2xl"
            style={{ animation: 'celebration-pop 0.4s ease-out' }}>
            <div className="text-6xl mb-3" role="img" aria-label="trophy">🏆</div>
            <div className="text-xs uppercase tracking-widest text-amber-600 font-bold mb-2">
              Bonus Unlocked
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              You earned {money(celebration.bonus_cents)}!
            </h2>
            <p className="text-slate-600 mb-1">
              <b>{celebration.tier_label}</b>
            </p>
            <p className="text-sm text-slate-500 mb-6">
              You hit {celebration.snapshot_value.toLocaleString()}{' '}
              {celebration.metric === 'revenue_cents' ? 'in revenue' : celebration.metric}.
              Your bonus is now pending brand approval.
            </p>
            <Button onClick={dismissCelebration}
              className="bg-amber-500 hover:bg-amber-600 text-white w-full"
              data-testid="celebration-dismiss-btn">
              Keep going →
            </Button>
            <style>{`
              @keyframes celebration-pop {
                0%   { transform: scale(0.85); opacity: 0; }
                60%  { transform: scale(1.04); opacity: 1; }
                100% { transform: scale(1);    opacity: 1; }
              }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid={testid}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-slate-50 rounded-lg p-2">
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm font-bold text-slate-900">{value}</div>
    </div>
  );
}
